import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { fetchDesigner } from '../firebase/designerService';
import { fetchPublicProfile } from '../firebase/publicProfileService';
import {
  subscribeComments, addComment, editComment, removeComment, COMMENT_MAX,
} from '../firebase/commentService';
import PersonIcon from './PersonIcon';
import './Comments.css';

// ── COMMENTS ON A PHOTO ──────────────────────────────────────────────────────
// One flat list with a single level of replies. Deeper nesting is a staircase
// on a phone, and a reply to a reply is still about the same top-level remark.
//
// Reads: ONE listener for the open photo. Author avatars come from the
// designer directory cache, which the surrounding page has usually already
// filled — see useAuthors below for why they are not stored on the comment.

/** Relative time, in the coarsest unit that is still accurate. */
function useAgo() {
  const { t } = useT();
  return useCallback((ts) => {
    const ms = ts?.toMillis ? ts.toMillis() : (ts?.seconds ? ts.seconds * 1000 : 0);
    if (!ms) return t('cm_now');                     // written, not yet acknowledged
    const s = Math.max(0, (Date.now() - ms) / 1000);
    if (s < 60) return t('cm_now');
    const units = [['cm_m', 60], ['cm_h', 3600], ['cm_d', 86400]];
    for (let i = units.length - 1; i >= 0; i -= 1) {
      const [key, size] = units[i];
      if (s >= size) return t(key).replace('{n}', Math.floor(s / size));
    }
    return t('cm_now');
  }, [t]);
}

/**
 * Avatars for the people in a thread.
 *
 * THE COMMENT STORES A NAME, NOT A PICTURE. An avatar is a data URL of up to
 * 120KB (constants/designer); copying one onto every comment would make
 * reading a thread cost megabytes and leave a stale portrait behind whenever
 * someone changed theirs — a bug this app has already had once, with chat.
 *
 * So they are resolved here, once per distinct author, through the designer
 * directory cache — which the profile or feed that opened this lightbox has
 * usually filled already, making it free. A commenter with no public profile
 * (a customer's document is private) simply keeps the default icon.
 */
function useAuthors(uids) {
  const [map, setMap] = useState({});
  const key = uids.join(',');

  useEffect(() => {
    let alive = true;
    const wanted = key ? key.split(',') : [];
    const missing = wanted.filter(u => u && !(u in map));
    if (!missing.length) return undefined;
    Promise.all(missing.map(u =>
      // The designer directory first — it is usually already cached by the
      // page that opened this lightbox, so those cost nothing. Anyone it does
      // not know is a customer, whose own document is owner-only; their public
      // card is the only readable source for a name and picture.
      fetchDesigner(u)
        .then(d => (d ? [u, d] : fetchPublicProfile(u).then(c => [
          u,
          c ? { fullName: c.name, avatarUrl: c.avatar, card: true } : null,
        ])))
        .catch(() => [u, null]),
    )).then((pairs) => {
      if (alive) setMap(prev => ({ ...prev, ...Object.fromEntries(pairs) }));
    });
    return () => { alive = false; };
    // Keyed on the joined uid list ONLY. `map` is read above but deliberately
    // not a dependency: this effect writes it, so listing it would re-run the
    // effect on every resolution it causes. The closure sees the current
    // render's map, which is what the "already have it?" test needs.
  }, [key]);

  return map;
}

// `col` is the gallery collection the item lives in — 'photos' or
// 'publishedDesigns'. Comments work identically on both; see commentService.
export default function Comments({ col, itemId, ownerUid, onCount, caption = null }) {
  const { user, profile } = useAuth();
  const { t } = useT();
  const ago = useAgo();

  const [items, setItems] = useState(null);   // null = still loading
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [editingId, setEditingId] = useState('');
  const [editDraft, setEditDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [menuFor, setMenuFor] = useState('');
  const inputRef = useRef(null);

  // ── ARRIVING FROM A NOTIFICATION ───────────────────────────────────────────
  // "X replied to you" links to `?photo=<id>&comment=<id>`. The photo half is
  // handled by the page that opens the lightbox; this is the other half —
  // scroll the thread to that reply and mark it, so a notification that
  // promised a particular comment delivers one instead of the top of a list.
  //
  // Read here rather than threaded down through the lightbox: the id names one
  // specific comment, so it can only ever match on the photo it belongs to.
  const [params, setParams] = useSearchParams();
  const wanted = params.get('comment');
  const [flash, setFlash] = useState('');
  const rowRefs = useRef({});

  useEffect(() => {
    if (!wanted || !items) return undefined;
    if (!items.some(c => c.id === wanted)) return undefined;
    setFlash(wanted);

    // After paint — the row does not exist to scroll to until it has rendered.
    const raf = requestAnimationFrame(() => {
      rowRefs.current[wanted]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    // Consumed, so a refresh does not re-flash it and the id does not travel
    // on into anything the user shares.
    setParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('comment');
      return next;
    }, { replace: true });

    const done = setTimeout(() => setFlash(''), 2400);
    return () => { cancelAnimationFrame(raf); clearTimeout(done); };
  }, [wanted, items, setParams]);

  useEffect(() => {
    setItems(null);
    setReplyTo(null);
    setEditingId('');
    if (!col || !itemId) return undefined;
    return subscribeComments(col, itemId, setItems);
  }, [col, itemId]);

  // Lets the lightbox show a live count without a second listener.
  useEffect(() => { if (items) onCount?.(items.length); }, [items, onCount]);

  // ── one level of threading ────────────────────────────────────────────────
  // Built once per change rather than filtering inside the render for every
  // row, which is O(n²) on a busy thread.
  const { roots, repliesOf } = useMemo(() => {
    const list = items || [];
    const ids = new Set(list.map(c => c.id));
    const byParent = new Map();
    const top = [];
    list.forEach((c) => {
      // A reply whose parent has been deleted is promoted to the top level
      // rather than vanishing with it — the remark still stands on its own,
      // and nothing cascades deletes (see commentService).
      if (c.parentId && ids.has(c.parentId)) {
        const arr = byParent.get(c.parentId) || [];
        arr.push(c);
        byParent.set(c.parentId, arr);
      } else {
        top.push(c);
      }
    });
    return { roots: top, repliesOf: byParent };
  }, [items]);

  const authors = useAuthors(useMemo(
    () => [...new Set((items || []).map(c => c.uid))].sort(),
    [items],
  ));

  const post = async (e) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text || !user?.uid || busy) return;
    setBusy(true);
    try {
      await addComment(col, itemId, {
        uid: user.uid,
        name: user.displayName || user.email?.split('@')[0] || '',
        text,
        parentId: replyTo?.id || null,
        // The person whose comment you pressed Reply under — not the thread
        // root that `parentId` points at. See addComment.
        replyToUid: replyTo?.uid || null,
      });
      setDraft('');
      setReplyTo(null);
    } catch (err) {
      console.warn('[comments] post failed:', err?.code || err);
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (c) => {
    const text = editDraft.trim();
    if (!text || text === c.text) { setEditingId(''); return; }
    try { await editComment(col, itemId, c.id, text); } catch (err) {
      console.warn('[comments] edit failed:', err?.code || err);
    }
    setEditingId('');
  };

  const del = async (c) => {
    setMenuFor('');
    try { await removeComment(col, itemId, c.id); } catch (err) {
      console.warn('[comments] delete failed:', err?.code || err);
    }
  };

  const startReply = (c) => {
    setReplyTo(c);
    inputRef.current?.focus();
  };

  // Close the row menu on any outside press.
  useEffect(() => {
    if (!menuFor) return undefined;
    const close = () => setMenuFor('');
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [menuFor]);

  const row = (c, isReply) => {
    // YOUR OWN COMMENTS USE THE PROFILE ALREADY IN MEMORY.
    //
    // `authors` resolves everyone else through the designer directory, which
    // reads `users/{uid}` — and a CUSTOMER's document is owner-only by rule, so
    // that lookup returns nothing for them and their own comments came back
    // with the default icon. Nobody needs a lookup to know what they look like:
    // AuthContext is holding it. Costs no read, and fixes the commonest case,
    // which is you looking at a thread you took part in.
    //
    // It does NOT fix a customer's picture as seen by OTHER people — that needs
    // a publicly readable profile card, which does not exist yet.
    const prof = (c.uid === user?.uid && profile)
      ? { fullName: profile.fullName, avatarUrl: profile.avatarUrl }
      : authors[c.uid];
    const name = prof?.fullName || c.name || t('cm_someone');
    const mine = !!user?.uid && user.uid === c.uid;
    // THE OWNER OF THE WORK MODERATES IT. Deleting is the only thing they get
    // on someone else's comment — the rules grant exactly that and no edit.
    const canDelete = mine || (!!user?.uid && user.uid === ownerUid);
    const editing = editingId === c.id;

    return (
      <li
        key={c.id}
        ref={el => { rowRefs.current[c.id] = el; }}
        className={`cm-row${isReply ? ' cm-row--reply' : ''}${flash === c.id ? ' cm-row--flash' : ''}`}
      >
        <span className="cm-avatar">
          {prof?.avatarUrl ? <img src={prof.avatarUrl} alt="" /> : <PersonIcon />}
        </span>

        <div className="cm-body">
          <div className="cm-head">
            {/* LINKED ONLY IF THERE IS A PAGE TO LINK TO. Keyed on `authors`,
                not on `prof` — `prof` now also covers your own profile from
                memory, and a customer has no /designer/:uid page, so linking
                on it would send you to a route that renders nothing. */}
            {authors[c.uid] && !authors[c.uid].card
              ? <Link to={`/designer/${c.uid}`} className="cm-name">{name}</Link>
              : <span className="cm-name">{name}</span>}
            <time className="cm-ago">{ago(c.createdAt)}</time>
            {c.editedAt && <span className="cm-edited">{t('cm_edited')}</span>}

            {(mine || canDelete) && !editing && (
              <span className="cm-actions">
                <button
                  type="button"
                  className="cm-act"
                  aria-label={t('chat_actions')}
                  onPointerDown={e => e.stopPropagation()}
                  onClick={() => setMenuFor(menuFor === c.id ? '' : c.id)}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                    <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" />
                  </svg>
                </button>
                {menuFor === c.id && (
                  <span className="cm-menu" onPointerDown={e => e.stopPropagation()}>
                    {mine && (
                      <button type="button" className="cm-menu-item"
                        onClick={() => { setMenuFor(''); setEditingId(c.id); setEditDraft(c.text); }}>
                        {t('chat_edit')}
                      </button>
                    )}
                    {canDelete && (
                      <button type="button" className="cm-menu-item cm-menu-item--danger"
                        onClick={() => del(c)}>
                        {t('delete_tooltip')}
                      </button>
                    )}
                  </span>
                )}
              </span>
            )}
          </div>

          {editing ? (
            <div className="cm-edit">
              <textarea
                className="cm-input"
                value={editDraft}
                maxLength={COMMENT_MAX}
                rows={2}
                onChange={e => setEditDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(c); }
                  if (e.key === 'Escape') setEditingId('');
                }}
              />
              <div className="cm-edit-row">
                <button type="button" className="cm-btn cm-btn--ghost" onClick={() => setEditingId('')}>
                  {t('cancel')}
                </button>
                <button type="button" className="cm-btn" onClick={() => saveEdit(c)}>
                  {t('save')}
                </button>
              </div>
            </div>
          ) : (
            <p className="cm-text">{c.text}</p>
          )}

          {/* A REPLY ALWAYS ATTACHES TO THE TOP-LEVEL COMMENT, so replying to a
              reply keeps the thread two deep instead of stepping right forever. */}
          {user?.uid && !editing && (
            <button type="button" className="cm-reply-btn"
              onClick={() => startReply(isReply ? { id: c.parentId, name, uid: c.uid } : c)}>
              {t('chat_reply')}
            </button>
          )}
        </div>
      </li>
    );
  };

  return (
    <section className="cm" aria-label={t('cm_title')}>
      <h3 className="cm-title">
        {t('cm_title')}
        {items && items.length > 0 && <span className="cm-count">{items.length}</span>}
      </h3>

      <div className="cm-scroll">
        {/* ── THE CAPTION ──────────────────────────────────────────────────
            A product's description, pinned above the thread.

            It used to be printed on the picture, and a shop's description is
            not a one-line label — a paragraph about the material and the
            dimensions covered the bottom third of the thing being sold. Here
            it has a column made for reading, it scrolls away as the thread
            grows, and it sits where every feed puts a caption: first, with the
            replies under it.

            Inside `cm-scroll` rather than above it on purpose: the panel is
            already short on a phone, and a fixed caption would eat the room
            the comments need. */}
        {caption && <p className="cm-caption">{caption}</p>}

        {items === null && <p className="cm-empty">{t('cm_loading')}</p>}
        {items && items.length === 0 && <p className="cm-empty">{t('cm_none')}</p>}
        {items && items.length > 0 && (
          <ul className="cm-list">
            {roots.map(c => (
              <React.Fragment key={c.id}>
                {row(c, false)}
                {(repliesOf.get(c.id) || []).map(r => row(r, true))}
              </React.Fragment>
            ))}
          </ul>
        )}
      </div>

      {user?.uid ? (
        <form className="cm-compose" onSubmit={post}>
          {replyTo && (
            <div className="cm-replying">
              <span>{t('cm_replying').replace('{name}', replyTo.name || t('cm_someone'))}</span>
              <button type="button" onClick={() => setReplyTo(null)} aria-label={t('cancel')}>✕</button>
            </div>
          )}
          <div className="cm-compose-row">
            <textarea
              ref={inputRef}
              className="cm-input"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder={t('cm_placeholder')}
              maxLength={COMMENT_MAX}
              rows={1}
              onKeyDown={(e) => {
                // Enter posts, Shift+Enter breaks the line — the convention
                // everywhere else in this app's inputs.
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); post(); }
              }}
            />
            <button type="submit" className="cm-btn" disabled={!draft.trim() || busy}>
              {t('cm_post')}
            </button>
          </div>
        </form>
      ) : (
        <p className="cm-signin">{t('cm_signin')}</p>
      )}
    </section>
  );
}
